/**
 * A metals inventory rack: a grid of bins, rows lettered A, B, C... and columns
 * numbered 1, 2, 3... (bin "B4" is the second row, fourth column). Mirrors the
 * plastics inventory area/location shape, collapsed into one row because metals
 * starts with a single rack.
 */
export interface MetalsInventoryRack {
    id: string;
    name: string;
    rows: number;
    columns: number;
    /** Printer every bin label on this rack goes to; '' until chosen in settings. */
    labelPrinterId: string;
}

/**
 * One slot on a rack. Bins exist for every grid position whether or not they hold
 * anything, so a printed label's QR (the bin id) stays valid when the product changes.
 * The bin quantity is the only inventory count for the product — MetalsProduct.qtyOnHand
 * is not touched.
 */
export interface MetalsInventoryBin {
    id: string;
    rackId: string;
    /** 0-based; 0 => row "A". */
    rowIndex: number;
    /** 0-based; 0 => column "1". */
    columnIndex: number;
    /** '' when the bin is empty. */
    productId: string;
    quantity: number;
}

/** Row letter for a 0-based row index: 0 => A, 25 => Z, 26 => AA. */
export function metalsInventoryRowLetter(rowIndex: number): string {
    let index = Math.max(0, Math.floor(rowIndex));
    let letters = '';
    do {
        letters = String.fromCharCode(65 + (index % 26)) + letters;
        index = Math.floor(index / 26) - 1;
    } while (index >= 0);
    return letters;
}

/** Grid position code for a bin, e.g. "A1". */
export function metalsInventoryBinCode(bin: Pick<MetalsInventoryBin, 'rowIndex' | 'columnIndex'>): string {
    return `${metalsInventoryRowLetter(bin.rowIndex)}${bin.columnIndex + 1}`;
}
